import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { createCompte, getComptes } from '../services/api'
import { useToast } from '../components/ToastProvider'
import Icon from '../components/Icon'
import Logo, { Wordmark } from '../components/Logo'

export default function PremierCompte() {
  const [nom, setNom] = useState('')
  const [montant, setMontant] = useState('')
  const [montantDecouvert, setMontantDecouvert] = useState('0')
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(true)
  const navigate = useNavigate()
  const toast = useToast()

  useEffect(() => {
    getComptes(true)
      .then((comptes) => {
        if (comptes && comptes.length > 0) {
          navigate('/dashboard', { replace: true })
        }
      })
      .catch(() => {})
      .finally(() => setChecking(false))
  }, [navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Les erreurs s'affichaient dans des alert() : elles bloquaient la page et
    // ne disaient pas quel champ corriger. Elles sont désormais sous le champ.
    const nextErrors = {}
    if (!nom.trim()) {
      nextErrors.nom = 'Donnez un nom à ce compte'
    }
    const montantNum = parseFloat(montant)
    if (montant === '' || Number.isNaN(montantNum)) {
      nextErrors.montant = 'Saisissez un solde'
    }
    const decouvertNum = parseFloat(montantDecouvert) || 0
    if (decouvertNum < 0) {
      nextErrors.decouvert = 'Le découvert ne peut pas être négatif'
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setLoading(true)
    try {
      await createCompte({
        nom: nom.trim(),
        montant: montantNum,
        montant_decouvert: decouvertNum,
      })
      navigate('/dashboard', { replace: true })
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || 'Erreur lors de la création du compte'
      )
    } finally {
      setLoading(false)
    }
  }

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <p className="text-base text-ink-muted" role="status">Chargement…</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-canvas px-4 py-12 sm:items-center">
      <div className="w-full max-w-[26rem]">
        <div className="mb-7 flex flex-col items-center text-center">
          <Logo size={40} />
          <Wordmark className="mt-3 text-xl text-ink" />
          <h1 className="mt-6 text-2xl font-semibold text-ink">Votre premier compte</h1>
          <p className="mt-1.5 text-base text-ink-secondary">
            Chaque opération sera rattachée à un compte. Vous pourrez en ajouter d’autres à tout
            moment.
          </p>
        </div>

        <div className="card p-6 sm:p-7">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="field">
              <label htmlFor="premier-nom" className="field-label">Nom du compte</label>
              <input
                id="premier-nom"
                type="text"
                autoFocus
                value={nom}
                onChange={(e) => {
                  setNom(e.target.value)
                  setErrors((c) => ({ ...c, nom: undefined }))
                }}
                placeholder="Compte courant"
                aria-invalid={!!errors.nom}
                className={`input ${errors.nom ? 'input-invalid' : ''}`}
              />
              {errors.nom && <p role="alert" className="field-error">{errors.nom}</p>}
            </div>

            <div className="field">
              <label htmlFor="premier-montant" className="field-label">Solde actuel</label>
              <input
                id="premier-montant"
                type="number"
                step="0.01"
                inputMode="decimal"
                value={montant}
                onChange={(e) => {
                  setMontant(e.target.value)
                  setErrors((c) => ({ ...c, montant: undefined }))
                }}
                placeholder="0,00"
                aria-invalid={!!errors.montant}
                className={`input tnum text-right ${errors.montant ? 'input-invalid' : ''}`}
              />
              {errors.montant && <p role="alert" className="field-error">{errors.montant}</p>}
            </div>

            <div className="field">
              <label htmlFor="premier-decouvert" className="field-label">
                Découvert autorisé <span className="font-normal text-ink-muted">(optionnel)</span>
              </label>
              <input
                id="premier-decouvert"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={montantDecouvert}
                onChange={(e) => {
                  setMontantDecouvert(e.target.value)
                  setErrors((c) => ({ ...c, decouvert: undefined }))
                }}
                aria-invalid={!!errors.decouvert}
                className={`input tnum text-right ${errors.decouvert ? 'input-invalid' : ''}`}
              />
              {errors.decouvert && <p role="alert" className="field-error">{errors.decouvert}</p>}
              <p className="field-hint">
                Le montant en dessous de zéro que votre banque vous autorise.
              </p>
            </div>

            <button type="submit" disabled={loading} className="btn-primary btn-block btn-lg">
              {loading && <Icon name="refresh" size={16} className="animate-spin" />}
              {loading ? 'Création…' : 'Créer le compte'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
